// @flow
import * as React from 'react';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import {
  selectCartCurrency,
  selectCartTotal,
  selectCartUUID,
  selectCountItems,
} from '@gandi/cart';

import { useTranslator } from '@gandi/translate';
import {
  Base,
  Button,
  Drawer,
  Text,
  TextPlaceholder,
  useDrawerState,
} from '@gandi/ui';

import { useAppSelector } from '../hooks/useAppSelector';

import { useCartTrayVisible } from '../hooks/useCartTrayVisible';
import { selectCurrentBU } from '../modules/organization/selectors';

import styles from './CartTray.scss';

function usePrice():
  | { formatted: string, mention: string, price: string }
  | { formatted: React.MixedElement } {
  const { __, formatPrice } = useTranslator();
  const currency = useAppSelector(selectCartCurrency);
  const totalProduct = useAppSelector(selectCartTotal);
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);
  const currentBU = useAppSelector(selectCurrentBU);

  const hasTaxes =
    !!totalProduct?.taxes?.length && totalProduct.taxes?.[0].rate !== 0;
  const showExcludeVatMention = hasTaxes && !displayTaxes;
  const withoutTaxes = showExcludeVatMention && currentBU !== 3;

  if (!currency) {
    return {
      formatted: <TextPlaceholder />,
    };
  }

  const price = withoutTaxes
    ? formatPrice(totalProduct.beforeTaxes, currency)
    : formatPrice(totalProduct.afterTaxes, currency);

  const mention = withoutTaxes ? __('excl. taxes') : __('incl. taxes');

  return {
    price,
    mention,
    formatted: `${price} ${mention}`,
  };
}

export function CartTray(): React.MixedElement {
  const { formatted } = usePrice();

  const drawer = useDrawerState();
  const { __ } = useTranslator();

  const { cartTrayDisplayed } = useCartTrayVisible();

  const productsCount = useAppSelector(selectCountItems);
  const cartId = useAppSelector(selectCartUUID);

  const configurationLink = `/cart/${cartId}/configure/account-choice`;

  React.useEffect(() => {
    if (cartTrayDisplayed) {
      drawer.show();
    } else {
      drawer.close();
    }
  }, [drawer, cartTrayDisplayed]);

  return (
    <Drawer {...drawer} position="bottom" className={styles.cartTray}>
      <Base flex flexJustifyEnd className={styles.mainWrapperCartTray}>
        <Base flex gap="small" flexAlignItemsCenter>
          <Text color="weak" size="big">
            {__('%(count)s product', { count: productsCount })}
          </Text>{' '}
          <Text size="big" weight="semibold">
            {formatted}
          </Text>
        </Base>

        <Button
          to={configurationLink}
          iconAfter="arrowRight"
          onClick={drawer.close}
        >
          {__('Continue')}
        </Button>
      </Base>
    </Drawer>
  );
}
